import React, { useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useGetMembersQuery } from '../store/api/portalApi';

const BILLING_ROLES = new Set(['owner', 'admin']);

/**
 * Whether the current user may access Payments (org owner or admin).
 * Mirrors API ``view_billing`` / ``manage_billing`` for system roles.
 */
export function useOrgBillingAccess(): {
  allowed: boolean;
  isLoading: boolean;
} {
  const { user, isInitializing } = useAuth();
  const orgId = user?.organizationId || '';
  const { data, isLoading: membersLoading } = useGetMembersQuery(
    { orgId, page: 1, pageSize: 100 },
    { skip: !orgId },
  );

  const allowed = useMemo(() => {
    if (!orgId || !data?.items?.length) return false;
    const me = data.items.find((m) => {
      if (user?.id && m.userId === user.id) return true;
      // Cognito ``sub`` is not the RDBMS users.id stored on members.
      if (user?.email && m.email && m.email.toLowerCase() === user.email.toLowerCase()) {
        return true;
      }
      return false;
    });
    const role = (me?.roleName || '').toLowerCase();
    return BILLING_ROLES.has(role);
  }, [data?.items, orgId, user?.email, user?.id]);

  const isLoading = isInitializing || (!!orgId && membersLoading);

  return { allowed, isLoading };
}

/** Route guard — org owner/admin required for Payments. */
export const RequireOrgBilling: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { allowed, isLoading } = useOrgBillingAccess();
  if (isLoading) return null;
  if (!allowed) {
    return <Navigate to="/agents" replace />;
  }
  return <>{children}</>;
};
